import { useEffect, useState } from 'react'
import {
  activatePrompt,
  createPrompt,
  listPrompts,
  testPrompt,
  type AdminSystemPrompt,
} from '../lib/api'
import { streamChatEvents } from '../lib/sse'

export function PersonaPage() {
  const [prompts, setPrompts] = useState<AdminSystemPrompt[]>([])
  const [draftVersion, setDraftVersion] = useState('')
  const [draftText, setDraftText] = useState('')
  const [testMessage, setTestMessage] = useState('Was kannst du mir zeigen?')
  const [testOutput, setTestOutput] = useState('')
  const [testing, setTesting] = useState(false)

  async function refresh() {
    const { prompts } = await listPrompts()
    setPrompts(prompts)
    const active = prompts.find((p) => p.isActive) ?? prompts[0]
    if (active && !draftText) setDraftText(active.text)
  }

  useEffect(() => {
    void refresh()
  }, [])

  async function handleSaveVersion() {
    if (!draftVersion || !draftText) return
    await createPrompt(draftVersion, draftText)
    setDraftVersion('')
    await refresh()
  }

  async function handleTest() {
    setTesting(true)
    setTestOutput('')
    try {
      const response = await testPrompt(draftText, testMessage)
      for await (const event of streamChatEvents(response)) {
        if (event.type === 'token') setTestOutput((prev) => prev + event.delta)
      }
    } finally {
      setTesting(false)
    }
  }

  return (
    <div className="grid max-w-5xl gap-8 lg:grid-cols-2">
      <div className="space-y-4">
        <h2 className="page-title">Persönlichkeit</h2>
        <p className="text-sm text-text-muted">
          Änderungen hier gehen sofort live, ohne Deployment. Teste eine neue Fassung erst in der
          Testkonsole, bevor du sie speicherst.
        </p>
        <textarea
          className="input min-h-[55vh] font-mono leading-relaxed md:text-xs lg:min-h-0"
          rows={22}
          value={draftText}
          onChange={(e) => setDraftText(e.target.value)}
        />
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            className="input"
            placeholder="Versionsname, z. B. v2"
            value={draftVersion}
            onChange={(e) => setDraftVersion(e.target.value)}
          />
          <button
            onClick={() => void handleSaveVersion()}
            disabled={!draftVersion.trim() || !draftText.trim()}
            className="btn-primary shrink-0"
          >
            Als neue Version speichern
          </button>
        </div>

        <div className="pt-4">
          <h3 className="mb-2 font-medium">Versionen</h3>
          <ul className="card divide-y divide-border overflow-hidden text-sm">
            {prompts.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <span className="min-w-0 truncate">
                  {p.version} {p.isActive && <span className="text-status-active">● aktiv</span>}
                </span>
                <div className="flex shrink-0 gap-2">
                  <button
                    onClick={() => {
                      setDraftText(p.text)
                      window.scrollTo({ top: 0, behavior: 'smooth' })
                    }}
                    className="btn-chip"
                  >
                    Laden
                  </button>
                  {!p.isActive && (
                    <button
                      onClick={() => void activatePrompt(p.id).then(refresh)}
                      className="btn-chip text-status-active"
                    >
                      Aktivieren
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="section-title">Testkonsole</h2>
        <p className="text-sm text-text-muted">Probiert den Entwurf aus, ohne ihn zu speichern.</p>
        <textarea
          className="input"
          rows={3}
          value={testMessage}
          onChange={(e) => setTestMessage(e.target.value)}
          placeholder="Testnachricht"
        />
        <button
          onClick={() => void handleTest()}
          disabled={testing}
          className="btn-primary w-full sm:w-auto"
        >
          {testing ? 'Antwortet …' : 'Testen'}
        </button>
        <div className="card min-h-[120px] p-5 text-sm whitespace-pre-wrap">
          {testOutput || <span className="text-text-muted">Antwort erscheint hier.</span>}
        </div>
      </div>
    </div>
  )
}
